import { treaty } from '@elysiajs/eden';
import type { App } from '@telesafi/api/types';

export type CreateClientOptions = {
  baseUrl: string;
  /** Site slug sent on every request as the X-Site-Slug header. */
  siteSlug?: string;
  fetcher?: typeof fetch;
  headers?: Record<string, string>;
};

/**
 * Single API client factory used everywhere (browser + SSR worker + admin).
 * Sends cookies for auth flows; site slug travels in the X-Site-Slug header.
 */
export function createApiClient(opts: CreateClientOptions) {
  return treaty<App>(opts.baseUrl, {
    fetcher: opts.fetcher,
    fetch: { credentials: 'include' },
    headers: {
      ...(opts.siteSlug ? { 'X-Site-Slug': opts.siteSlug } : {}),
      ...(opts.headers ?? {}),
    },
  });
}

export type ApiClient = ReturnType<typeof createApiClient>;

// Back-compat aliases for callers still on the older names. Both produce
// the same client now; the internal-secret flow is gone.
export const createBrowserClient = createApiClient;
export const createServerClient = (
  opts: CreateClientOptions & { internalSecret?: string },
): ApiClient =>
  createApiClient({
    ...opts,
    headers: {
      ...(opts.internalSecret ? { 'X-Internal-Secret': opts.internalSecret } : {}),
      ...(opts.headers ?? {}),
    },
  });
export type BrowserApiClient = ApiClient;
export type ServerApiClient = ApiClient;
